// HoaDetailPage — /hoas/:hoaId route (§8.2 HOA detail page).
//
// Three panels:
//   1. Header with editable name.
//   2. Properties associated — add via searchable dropdown + remove via per-row button.
//   3. CC&R Documents — list + upload new version.
//   Danger panel: Delete HOA.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getHoaDetail,
  updateHoaName,
  updateHoaDefaultFee,
  deleteHoa,
  uploadHoaCcr,
  getCcrDetail,
  setPropertyHoa,
  listProperties,
  type HoaDetail,
  type AssociatedProperty,
  type PropertySummary,
  type CcrVersionRecord,
} from '@/lib/hoasClient';
import { ApiError } from '@/lib/api';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { MoneyInput } from '@/components/MoneyInput';
import { SearchSelect } from '@/components/SearchSelect';

export function HoaDetailPage() {
  const { hoaId } = useParams<{ hoaId: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<HoaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Name editing
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  // Property picker (searchable dropdown)
  const [allProperties, setAllProperties] = useState<PropertySummary[]>([]);
  const [addPropError, setAddPropError] = useState<string | null>(null);
  const [addingProp, setAddingProp] = useState(false);

  // Default fee editor
  const [draftFeeCents, setDraftFeeCents] = useState<number>(0);
  const [savingFee, setSavingFee] = useState(false);
  const [feeError, setFeeError] = useState<string | null>(null);

  // CC&R upload + per-CCR action errors (preview, download original)
  const ccrInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [ccrError, setCcrError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Delete HOA
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadDetail = useCallback(async () => {
    if (!hoaId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getHoaDetail(hoaId);
      setDetail(data);
      setDraftName(data.hoa.name);
      setDraftFeeCents(data.hoa.defaultFeeCents ?? 0);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load HOA');
    } finally {
      setLoading(false);
    }
  }, [hoaId]);

  useEffect(() => {
    void loadDetail();
    // Load full property list for the Add property picker.
    listProperties().then(setAllProperties).catch(() => {/* non-fatal */});
  }, [loadDetail]);

  async function handleSaveName() {
    if (!hoaId || !draftName.trim()) return;
    setSavingName(true);
    setNameError(null);
    try {
      await updateHoaName(hoaId, draftName.trim());
      setEditingName(false);
      await loadDetail();
    } catch (err) {
      setNameError(err instanceof Error ? err.message : 'Failed to save name');
    } finally {
      setSavingName(false);
    }
  }

  async function handleSaveFee() {
    if (!hoaId) return;
    setSavingFee(true);
    setFeeError(null);
    try {
      // Empty / 0 stores as null (no default fee on this HOA).
      await updateHoaDefaultFee(hoaId, draftFeeCents > 0 ? draftFeeCents : null);
      await loadDetail();
    } catch (err) {
      setFeeError(err instanceof Error ? err.message : 'Failed to save fee');
    } finally {
      setSavingFee(false);
    }
  }

  async function handleAddProperty(propertyId: string) {
    if (!hoaId || !propertyId) return;
    setAddingProp(true);
    setAddPropError(null);
    try {
      await setPropertyHoa(propertyId, hoaId);
      await loadDetail();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string } | null;
        setAddPropError(body?.error ?? 'Conflict: the HOA may have been deleted. Refresh the page.');
      } else {
        setAddPropError(err instanceof Error ? err.message : 'Failed to add property');
      }
    } finally {
      setAddingProp(false);
    }
  }

  async function handleRemoveProperty(propertyId: string) {
    if (!hoaId) return;
    setRemoveError(null);
    try {
      // Phase E Codex S3: assert the property is still on THIS HOA via
      // expectedHoaId. Without it, a stale page would silently clear a newer
      // association created by another staff user. Server 409s on mismatch.
      await setPropertyHoa(propertyId, null, hoaId);
      await loadDetail();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string; currentHoaId?: string | null } | null;
        setRemoveError(
          body?.error
            ?? 'Property association changed since you viewed this page. Refreshing.'
        );
        await loadDetail();
        return;
      }
      setRemoveError(err instanceof Error ? err.message : 'Failed to remove property');
    }
  }

  async function handleCcrUpload(file: File) {
    if (!hoaId) return;
    setUploading(true);
    setUploadError(null);
    try {
      await uploadHoaCcr(hoaId, file);
      await loadDetail();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        if (err.status === 415) {
          setUploadError('Only PDF files are accepted.');
        } else if (err.status === 413) {
          setUploadError('File too large. Maximum size is 50 MB.');
        } else {
          setUploadError(body?.error ?? 'Upload failed.');
        }
      } else {
        setUploadError(err instanceof Error ? err.message : 'Upload failed.');
      }
    } finally {
      setUploading(false);
      if (ccrInputRef.current) ccrInputRef.current.value = '';
    }
  }

  async function handlePreviewCcr(ccr: CcrVersionRecord) {
    if (!hoaId) return;
    setCcrError(null);
    try {
      const detail = await getCcrDetail(hoaId, ccr.versionId);
      window.open(detail.optimizedUrl, '_blank');
    } catch (err) {
      setCcrError(err instanceof Error ? err.message : 'Failed to get download URL');
    }
  }

  async function handleDownloadOriginal(ccr: CcrVersionRecord) {
    if (!hoaId) return;
    setCcrError(null);
    try {
      const detail = await getCcrDetail(hoaId, ccr.versionId);
      window.open(detail.originalUrl, '_blank');
    } catch (err) {
      setCcrError(err instanceof Error ? err.message : 'Failed to get download URL');
    }
  }

  async function handleDeleteHoa() {
    if (!hoaId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteHoa(hoaId);
      navigate('/hoas');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string } | null;
        setDeleteError(body?.error ?? 'This HOA still has associated properties.');
      } else {
        setDeleteError(err instanceof Error ? err.message : 'Failed to delete HOA');
      }
    } finally {
      setDeleting(false);
    }
  }

  // Properties available to add (those not already associated).
  const associatedIds = new Set(detail?.properties.map((p) => p.propertyId) ?? []);
  const availableProperties = allProperties.filter((p) => !associatedIds.has(p.propertyId));

  if (loading) return <p style={{ color: 'var(--fg-muted)' }}>Loading…</p>;
  if (loadError) {
    return (
      <div className="alert" data-tone="danger">
        <div className="alert-body">{loadError}</div>
      </div>
    );
  }
  if (!detail) return null;

  const { hoa, properties, ccrs } = detail;

  return (
    <div style={{ maxWidth: 800 }}>
      {/* Header + name */}
      <div style={{ marginBottom: 'var(--space-7)' }}>
        <button
          type="button"
          className="btn"
          data-variant="link"
          data-size="sm"
          onClick={() => navigate('/hoas')}
          style={{ padding: 0, marginBottom: 'var(--space-3)', height: 'auto' }}
        >
          ← Back to HOAs
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
          {editingName ? (
            <>
              <input
                className="input"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleSaveName();
                  if (e.key === 'Escape') setEditingName(false);
                }}
                style={{ width: 320, fontWeight: 'var(--weight-bold)' }}
                autoFocus
              />
              <button
                type="button"
                className="btn"
                data-size="sm"
                onClick={() => void handleSaveName()}
                disabled={savingName}
              >
                {savingName ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                onClick={() => setEditingName(false)}
              >
                Cancel
              </button>
              {nameError && (
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--danger)' }}>{nameError}</span>
              )}
            </>
          ) : (
            <>
              <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
                {hoa.name}
              </h1>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                onClick={() => { setEditingName(true); setNameError(null); }}
              >
                Rename
              </button>
            </>
          )}
        </div>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-subtle)', marginTop: 'var(--space-2)' }}>
          ID: {hoa.hoaId} · Last updated {new Date(hoa.updatedAt).toLocaleDateString()} by {hoa.updatedByDisplay}
        </p>
      </div>

      {/* Default fee panel */}
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="card-header">
          <h2 className="card-title">Default monthly fee</h2>
        </div>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: '0 0 var(--space-3)' }}>
          Leases against this HOA pre-fill the HOA Fee row of the §2 rent table
          with this default. Staff can override per-lease on the configure page.
          Leave at $0 to omit by default.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <div style={{ width: 180 }}>
            <MoneyInput
              valueCents={draftFeeCents}
              onChangeCents={(c) => setDraftFeeCents(c)}
              ariaLabel="Default monthly HOA fee"
            />
          </div>
          <button
            type="button"
            className="btn"
            data-size="sm"
            onClick={() => void handleSaveFee()}
            disabled={savingFee || draftFeeCents === (hoa.defaultFeeCents ?? 0)}
          >
            {savingFee ? 'Saving…' : 'Save'}
          </button>
          {feeError && (
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--danger)' }}>{feeError}</span>
          )}
        </div>
      </div>

      {/* Properties panel */}
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="card-header">
          <h2 className="card-title">Properties ({properties.length})</h2>
        </div>
        {removeError && (
          <div
            className="alert"
            data-tone="danger"
            role="alert"
            style={{ marginBottom: 'var(--space-4)' }}
          >
            <div className="alert-body">{removeError}</div>
          </div>
        )}
        {properties.length === 0 && (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-subtle)', margin: '0 0 var(--space-4)' }}>
            No properties associated.
          </p>
        )}
        {properties.map((p: AssociatedProperty) => (
          <div
            key={p.propertyId}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-3) 0',
              borderBottom: '1px solid var(--border-subtle)',
            }}
          >
            <div>
              <div style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--weight-medium)', color: 'var(--fg)' }}>
                {p.name}
              </div>
              {p.address && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>{p.address}</div>
              )}
            </div>
            <button
              type="button"
              className="btn"
              data-variant="danger-ghost"
              data-size="sm"
              onClick={() => void handleRemoveProperty(p.propertyId)}
            >
              Remove
            </button>
          </div>
        ))}
        <div className="field" style={{ marginTop: 'var(--space-5)' }}>
          <label htmlFor="hoa-add-property">Add property</label>
          <SearchSelect
            id="hoa-add-property"
            options={availableProperties.map((p) => ({
              value: p.propertyId,
              label: p.name,
              sublabel: p.address,
            }))}
            value=""
            onChange={(value) => {
              if (value) void handleAddProperty(value);
            }}
            disabled={addingProp || availableProperties.length === 0}
            placeholder={
              availableProperties.length === 0
                ? 'No properties available'
                : 'Select or search by name or address…'
            }
            emptyText="No matching properties"
          />
          {addPropError && (
            <span className="helper" data-state="error">{addPropError}</span>
          )}
        </div>
      </div>

      {/* CC&R Documents panel */}
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="card-header">
          <h2 className="card-title">CC&amp;R Documents ({ccrs.length})</h2>
        </div>
        {ccrError && (
          <div
            className="alert"
            data-tone="danger"
            role="alert"
            style={{ marginBottom: 'var(--space-4)' }}
          >
            <div className="alert-body">{ccrError}</div>
          </div>
        )}
        {ccrs.length === 0 && (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-subtle)', margin: '0 0 var(--space-4)' }}>
            No CC&amp;R documents uploaded yet.
          </p>
        )}
        {ccrs.map((ccr: CcrVersionRecord) => (
          <div
            key={ccr.versionId}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-3) 0',
              borderBottom: '1px solid var(--border-subtle)',
              gap: 'var(--space-4)',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--weight-medium)', color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {ccr.originalFilename}
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
                Uploaded {new Date(ccr.uploadedAt).toLocaleDateString()} by {ccr.uploadedByDisplay}
                {' · '}
                {Math.round(ccr.optimizedPdfSizeBytes / 1024)} KB
                {ccr.optimizationStatus === 'fallback' && (
                  <span style={{ marginLeft: 'var(--space-2)', color: 'var(--warning-fg)' }}>
                    (optimization failed — original used)
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flex: 'none' }}>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                onClick={() => void handlePreviewCcr(ccr)}
              >
                Preview PDF
              </button>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                onClick={() => void handleDownloadOriginal(ccr)}
              >
                Download original
              </button>
            </div>
          </div>
        ))}
        <div className="field" style={{ marginTop: 'var(--space-5)' }}>
          <label htmlFor="hoa-ccr-upload">Upload new version (PDF only, max 50 MB)</label>
          <input
            id="hoa-ccr-upload"
            ref={ccrInputRef}
            type="file"
            accept="application/pdf"
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleCcrUpload(file);
            }}
            style={{ fontSize: 'var(--text-sm)' }}
          />
          {uploading && (
            <span className="helper">Uploading…</span>
          )}
          {uploadError && (
            <span className="helper" data-state="error">{uploadError}</span>
          )}
        </div>
      </div>

      {/* Danger zone */}
      <div className="card" data-tone="danger">
        <div className="card-header">
          <h2 className="card-title">Danger Zone</h2>
        </div>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--danger-fg)', margin: '0 0 var(--space-4)', lineHeight: 'var(--lh-body)' }}>
          Deleting this HOA removes the HOA record. CC&amp;R version records and
          their S3 objects are retained (orphaned) for audit / future recovery.
          All associated properties must be disassociated first.
        </p>
        <button
          type="button"
          className="btn"
          data-variant="danger"
          onClick={() => { setShowDelete(true); setDeleteError(null); }}
        >
          Delete HOA
        </button>
      </div>

      <ConfirmDialog
        open={showDelete}
        onOpenChange={(open) => { if (!open) { setShowDelete(false); setDeleteError(null); } }}
        title={`Delete ${hoa.name}?`}
        description="This will permanently delete the HOA. This action cannot be undone. All property associations must be removed first."
        confirmLabel="Delete"
        busyLabel="Deleting…"
        busy={deleting}
        error={deleteError}
        onConfirm={() => void handleDeleteHoa()}
      />
    </div>
  );
}
